/**
 * SIGN IN — /login
 *
 * Sibling of the CRM sign-in: the same always-dark gold-on-charcoal panel on
 * the left and a theme-following form on the right. The panel shows the
 * console's own world — the companies it runs and the audit trail of every
 * change — instead of a tenant's inbox. It is illustrative only, so it never
 * claims a live platform status.
 */
import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AxiosError } from 'axios';
import clsx from 'clsx';
import {
  ArrowBigUp, ArrowRight, CircleAlert, CircleCheck, CodeXml, Eye, EyeOff, History, LockKeyhole, Mail, Scissors, ShieldCheck,
  ShoppingBag, UtensilsCrossed,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { ThemeToggle } from '@/components/ThemeToggle';
import { BrandMark, BrandTile } from '@/components/BrandMark';

type FieldErrors = Partial<Record<'email' | 'password', string>>;

export default function Login() {
  const { loginWithPassword } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const clearError = (key: keyof FieldErrors) => {
    setError('');
    setFieldErrors((fields) => ({ ...fields, [key]: undefined }));
  };
  const signInWithPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || success) return;
    const errors: FieldErrors = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errors.email = 'Enter a valid email address.';
    if (!password) errors.password = 'Enter your password.';
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    setBusy(true);
    setError('');
    try {
      await loginWithPassword(email.trim(), password);
      setSuccess(true);
      setTimeout(() => nav('/dashboard', { replace: true }), 420);
    } catch (err) {
      const response = (err as AxiosError<{ message?: string }>)?.response;
      setError(response?.status === 401 ? "That email and password don't match."
        : response?.status === 429 ? 'Too many attempts. Please wait before trying again.'
        : response?.data?.message ?? 'Unable to sign in. Check your connection and try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-screen bg-bg">
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
        <ConsolePanel />

        <div className="relative flex flex-col">
          <div className="flex items-center justify-between px-6 pt-5 sm:px-10">
            <div className="flex items-center gap-2.5 lg:invisible">
              <BrandTile size={36} />
              <div className="leading-tight">
                <p className="font-display text-[15px] font-bold tracking-tight text-text-primary">Slightor</p>
                <p className="text-2xs text-text-muted">Platform console</p>
              </div>
            </div>
            <ThemeToggle />
          </div>

          <main className="flex flex-1 items-center justify-center px-6 pb-10 pt-6 sm:px-10">
            <div className="login-form w-full max-w-[400px]">
              <h1 className="font-display text-[30px] font-bold leading-tight tracking-tight text-text-primary">Welcome back</h1>
              <p className="mt-1.5 text-sm text-text-muted">Sign in to the Slightor platform console.</p>

              {error && (
                <div role="alert" className="mt-6 flex items-start gap-2.5 rounded-xl border border-danger/20 bg-danger-soft px-3.5 py-3 text-sm text-danger">
                  <CircleAlert size={17} className="mt-0.5 shrink-0" />{error}
                </div>
              )}

              <form noValidate onSubmit={signInWithPassword} className="mt-7">
                <FormField label="Email address" htmlFor="login-email" error={fieldErrors.email}>
                  <Input
                    id="login-email"
                    type="email"
                    autoFocus
                    autoComplete="username"
                    placeholder="you@company.com"
                    value={email}
                    invalid={!!fieldErrors.email}
                    iconLeft={<Mail size={16} />}
                    className="h-11"
                    onChange={(e) => { setEmail(e.target.value); clearError('email'); }}
                  />
                </FormField>
                <FormField
                  label="Password"
                  htmlFor="login-password"
                  error={fieldErrors.password}
                  aside={capsLock ? (
                    <span className="inline-flex items-center gap-1 text-2xs font-medium text-warning">
                      <ArrowBigUp size={12} /> Caps Lock is on
                    </span>
                  ) : undefined}
                >
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    invalid={!!fieldErrors.password}
                    iconLeft={<LockKeyhole size={16} />}
                    className="h-11"
                    onChange={(e) => { setPassword(e.target.value); clearError('password'); }}
                    onKeyDown={(e) => setCapsLock(e.getModifierState('CapsLock'))}
                    onKeyUp={(e) => setCapsLock(e.getModifierState('CapsLock'))}
                    onBlur={() => setCapsLock(false)}
                    iconRight={(
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        aria-pressed={showPassword}
                        className="rounded-md p-2 text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    )}
                  />
                </FormField>
                <Button type="submit" size="lg" loading={busy} iconRight={<ArrowRight size={16} />} className="mt-2 w-full">
                  {busy ? 'Signing in…' : 'Sign in'}
                </Button>
              </form>

              <p className="mt-5 text-center text-xs leading-relaxed text-text-muted">
                This console is for Slightor administrators. Company teams sign in to their own workspace in the CRM.
              </p>
              <p className="mt-8 flex items-center justify-center gap-1.5 text-2xs text-text-muted lg:hidden">
                <ShieldCheck size={12} /> Every sign-in attempt is audited. © {new Date().getFullYear()} Mi Coders
              </p>
            </div>
          </main>

          {success && (
            <div role="status" className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 bg-bg/95 backdrop-blur-sm">
              <span className="login-seal flex h-16 w-16 items-center justify-center rounded-full bg-success-soft text-success">
                <CircleCheck size={32} strokeWidth={2} />
              </span>
              <p className="text-sm font-semibold text-text-primary">Signed in. Opening the console…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── console panel ────────────────────────────────────────────────────────

type Tone = 'good' | 'gold' | 'warn';
const TONE: Record<Tone, string> = {
  good: 'bg-[#88B362]/15 text-[#A8CF84]',
  gold: 'bg-[#C8A55B]/20 text-[#E5C77D]',
  warn: 'bg-[#E0A34A]/15 text-[#F0C27E]',
};

const COMPANIES: Array<{ name: string; detail: string; icon: typeof Mail; tile: string; status: string; tone: Tone }> = [
  { name: 'Saffron Bay Café', detail: 'Food, Growth plan, 6 users', icon: UtensilsCrossed, tile: 'bg-[#C8A55B]/15 text-[#E5C77D]', status: 'Healthy', tone: 'good' },
  { name: 'Nexa Digital', detail: 'Technology, Scale plan, 14 users', icon: CodeXml, tile: 'bg-[#7AA2D6]/15 text-[#A9C4EA]', status: 'Trial ends in 3 days', tone: 'gold' },
  { name: 'Luma Beauty Lounge', detail: 'Salon, Starter plan, 3 users', icon: Scissors, tile: 'bg-[#D98CA6]/15 text-[#EDB3C6]', status: 'Reconnect WhatsApp', tone: 'warn' },
  { name: 'Dune Outdoor Co.', detail: 'Retail, Growth plan, 9 users', icon: ShoppingBag, tile: 'bg-[#88B362]/15 text-[#B5D48F]', status: 'AI credits 82% used', tone: 'warn' },
];

const AUDIT = [
  { what: 'Company created', who: 'Saffron Bay Café', at: '09:41' },
  { what: 'Modules updated', who: 'Nexa Digital', at: '09:12' },
  { what: 'AI credits granted', who: 'Luma Beauty Lounge', at: '08:57' },
];

/** Always dark: what the console runs, drawn as a glimpse of the company list and its audit log. */
function ConsolePanel() {
  return (
    <aside
      aria-label="About the Slightor platform console"
      className="relative hidden overflow-hidden bg-[#121214] text-[#F4F1EB] lg:flex lg:flex-col lg:justify-between lg:gap-10 lg:p-12 xl:p-14"
      style={{
        backgroundImage: 'radial-gradient(55% 45% at 100% 0%, rgba(200,165,91,0.24) 0%, transparent 62%), radial-gradient(50% 40% at 0% 100%, rgba(200,165,91,0.14) 0%, transparent 60%)',
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          maskImage: 'radial-gradient(ellipse 72% 62% at 58% 48%, #000 22%, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(ellipse 72% 62% at 58% 48%, #000 22%, transparent 78%)',
        }}
      />

      <div className="relative flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#1A1A1A] ring-1 ring-white/10"><BrandMark size={24} glow /></span>
        <div className="leading-tight">
          <div className="font-display text-lg font-bold tracking-tight">Slightor</div>
          <div className="text-2xs uppercase tracking-[0.2em] text-[#9A958A]">Platform console</div>
        </div>
      </div>

      <div className="relative max-w-[520px]">
        <h2 className="font-display text-[42px] font-bold leading-[1.06] tracking-[-0.035em] xl:text-[46px]">
          Every company on Slightor.
          <span className="block bg-gold-gradient bg-clip-text text-transparent">Run from one console.</span>
        </h2>
        <p className="mt-5 max-w-[470px] text-[15px] leading-relaxed text-[#B5AE9F]">
          Create companies with the right plan and modules, keep their WhatsApp numbers and ad accounts connected,
          manage AI providers and credits, and see every change in the audit log.
        </p>

        <div aria-hidden className="relative mt-9 pb-28 [@media(max-height:859px)]:pb-0">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] backdrop-blur-md">
            <div className="flex items-center justify-between px-1.5 pb-2.5 pt-0.5">
              <span className="text-xs font-semibold">Companies</span>
              <span className={clsx('rounded-full px-2 py-0.5 text-[10px] font-semibold', TONE.warn)}>2 need attention</span>
            </div>
            <div className="space-y-1">
              {COMPANIES.map((company) => {
                const Icon = company.icon;
                return (
                  <div key={company.name} className="flex items-center gap-3 rounded-xl px-2 py-2">
                    <span className={clsx('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', company.tile)}><Icon size={14} /></span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12.5px]">{company.name}</div>
                      <div className="truncate text-[10px] text-[#8A8170]">{company.detail}</div>
                    </div>
                    <span className={clsx('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold', TONE[company.tone])}>{company.status}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-1 border-t border-white/[0.06] px-2 pb-1 pt-2.5 text-[10px] text-[#8A8170]">Showing 4 of 38 companies</div>
          </div>

          <div className="absolute bottom-0 right-[-16px] w-[244px] rounded-2xl border border-white/10 bg-[#1B1B1F]/95 p-3.5 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.7)] xl:right-[-32px] [@media(max-height:859px)]:hidden">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#C8A55B]/20 text-[#E5C77D]"><History size={12} /></span>
              <span className="text-[11.5px] font-semibold">Audit log</span>
            </div>
            <ol className="mt-3 space-y-2.5">
              {AUDIT.map((entry) => (
                <li key={entry.what} className="flex items-start gap-2.5">
                  <span className="mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#C8A55B]" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[11px]">{entry.what}</div>
                    <div className="truncate text-[10px] text-[#8A8170]">{entry.who}</div>
                  </div>
                  <span className="shrink-0 text-[10px] tabular-nums text-[#8A8170]">{entry.at}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <div className="relative mt-16 flex items-center justify-between gap-6 text-xs text-[#8A8170] [@media(max-height:859px)]:mt-2">
        <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} /> Administrators only. Every sign-in attempt is recorded in the audit log.</span>
        <span className="shrink-0">© {new Date().getFullYear()} Mi Coders</span>
      </div>
    </aside>
  );
}
